import React, { useEffect, useRef, useState } from 'react';
import { View, Text, ActivityIndicator, Platform, ImageBackground, TouchableOpacity } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import type { WatchPartyRecordingState } from '../../types/auth';
import { useRecordingPlaybackTelemetry } from '../../hooks/useRecordingPlaybackTelemetry';
import type { RecordingPlaybackSource } from '../../hooks/useRecordingPlaybackTelemetry';
import { RoomRecordingVideo } from './RoomRecordingVideo';
import { hasVideoFrame } from './roomMediaVideoTrack';
import { recordAudioOnlyProbe } from './recordingAudioOnlyStore';
import { useStableMediaUrl } from '../../utils/recordingMediaUrl';
import { isHlsUrl } from '../../utils/hlsPlayback';

/** CORE-1573 — высота полосы управления у записи без изображения. */
const AUDIO_ONLY_CONTROLS_HEIGHT = 56;

interface Props {
  playbackUrl?: string | null;
  state?: WatchPartyRecordingState;
  /**
   * Practice cover used as a visual surface when there's nothing to play
   * yet (state=None / Failed). Owner directive 2026-05-08: don't show
   * «Запись недоступна» on a flat dark frame — render the cover with a
   * soft «ЭФИР ЗАВЕРШЁН» badge so the screen still reads as the practice
   * the user attended, not «something missing».
   */
  coverImageUrl?: string | null;
  /**
   * CORE-1383 — есть ли в файле изображение, по данным бэкенда
   * (`GET /api/watch-parties/{id}/recording`, см. `useRecordingHasVideo`).
   * `false` — аудио-состояние рисуется сразу, не дожидаясь `loadedmetadata`
   * (CORE-1573 узнавал это только по кадру 0 × 0), и на нативе тоже.
   * `null`/отсутствие — не замерено: прежнее поведение.
   */
  hasVideo?: boolean | null;
  /**
   * CORE-859 — when the room ended (ISO string). Anchors the processing
   * timeout: a Recording/Processing state that outlives
   * {@link RECORDING_PROCESSING_TIMEOUT_MS} since this instant flips from
   * «Запись готовится» to a terminal fallback, so an infinite Processing
   * cannot exist even if the backend never sends a terminal event.
   */
  endedAtUtc?: string | null;
  /** Optional analytics context. When provided, recording_play_* events
   *  fire from the underlying <video>. Pass `roomId` and the surface
   *  the player is mounted on (typically `room_summary`). */
  telemetry?: {
    roomId: number;
    source: RecordingPlaybackSource;
    isOwner?: boolean;
  };
  /**
   * CORE-1780 — запись входит в подписку Agapēlla, а у зрителя её нет
   * (`isAgapellaLocked`): сервер не отдал ссылку. Без этого пустая ссылка
   * читалась как «LIVE-СЕССИЯ ЗАВЕРШЕНА» — будто записи нет.
   */
  locked?: boolean;
  /** CORE-1780 — кнопка закрытой записи: окно подписки. */
  onOpenPaywall?: () => void;
}

/**
 * The visual variant the player should render. A pure mapping from BE
 * `WatchPartyRecordingState` → display intent — kept out of the JSX so
 * tests can lock the precedence rules without mounting the component.
 *
 *   - `video`        → playable recording (state === Ready + url present)
 *   - `processing`   → cloud is still working (Recording/Processing)
 *   - `failed`       → recording was attempted but produced nothing
 *                      (state === Failed). Причин несколько — молчащий канал
 *                      (CORE-229), оборвавшаяся запись (CORE-1327), нерабочий
 *                      рекордер (CORE-1329), — и по состоянию они не
 *                      различаются. Поэтому экран говорит «Запись не
 *                      сохранилась» и не называет причину: назвать одну из
 *                      нескольких значит соврать в остальных случаях.
 *   - `stalled`      → CORE-859: the recording is still in a non-terminal
 *                      state (Recording/Processing) but has outlived the
 *                      processing deadline — the BE went silent and never
 *                      sent a terminal event. Rendered as a terminal fallback
 *                      so «Запись готовится» can NEVER hang forever.
 *   - `unavailable`  → no recording at all (None) — the room simply wasn't
 *                      recorded; we show the practice cover as a nice surface.
 *   - `locked`       → CORE-1780: the recording exists (or is still being made)
 *                      but is included in the Agapēlla subscription and the
 *                      server withheld the link: cover + badge + paywall button.
 *                      Never over `failed` / `None` — there is nothing to unlock.
 */
export type RecordingDisplayKind = 'video' | 'processing' | 'failed' | 'stalled' | 'unavailable' | 'locked';

/**
 * How long a post-live recording may sit in a non-terminal state (Recording /
 * Processing), measured from эфир end, before the UI stops promising «готовится»
 * and shows a terminal fallback. Real cloud encodes finish in 3–5 min; 15 min is
 * a generous ceiling that only trips when the backend genuinely went silent.
 * CORE-859 — an infinite Processing must not exist.
 */
export const RECORDING_PROCESSING_TIMEOUT_MS = 15 * 60_000;

/** CORE-1780 — слова закрытой записи (как у закрытого эфира, CORE-1778). */
export const LOCKED_RECORDING_BADGE = 'Входит в Agapēlla';
export const LOCKED_RECORDING_TITLE = 'Запись входит в Agapēlla';
export const LOCKED_RECORDING_ACTION = 'Открыть доступ';

/**
 * True when a recording is still non-terminal (Recording/Processing) yet has
 * outlived {@link RECORDING_PROCESSING_TIMEOUT_MS} since the эфир ended. Pure so
 * the timeout precedence can be locked in tests without mounting the component
 * or faking timers. `nowMs` is injected for the same reason.
 */
export function isRecordingProcessingStalled(
  state: WatchPartyRecordingState | undefined,
  endedAtUtc: string | null | undefined,
  nowMs: number,
): boolean {
  if (state !== 'Recording' && state !== 'Processing') return false;
  if (!endedAtUtc) return false;
  const ended = Date.parse(endedAtUtc);
  if (Number.isNaN(ended)) return false;
  return nowMs - ended > RECORDING_PROCESSING_TIMEOUT_MS;
}

export function getRecordingDisplayKind(
  state: WatchPartyRecordingState | undefined,
  playbackUrl: string | null | undefined,
  opts?: { processingStalled?: boolean; locked?: boolean },
): RecordingDisplayKind {
  if (state === 'Ready' && playbackUrl) return 'video';
  // CORE-1780 — «закрыто» раньше «готовится» и «недоступно»: файл будет или
  // уже есть, просто не для этого зрителя без подписки.
  if (opts?.locked && (state === 'Ready' || state === 'Recording' || state === 'Processing')) return 'locked';
  if (state === 'Recording' || state === 'Processing') {
    // CORE-859 — a non-terminal state that has outlived the deadline stops
    // promising «готовится» and falls back to a terminal message.
    return opts?.processingStalled ? 'stalled' : 'processing';
  }
  // CORE-229 — Failed is distinct from None: a Failed recording was attempted
  // and captured nothing, so it must NOT borrow None's success-looking
  // «ЭФИР ЗАВЕРШЁН» badge (that's the "looks saved but isn't" complaint).
  if (state === 'Failed') return 'failed';
  return 'unavailable';
}

const FRAME_STYLE = {
  width: '100%' as const,
  aspectRatio: 16 / 9,
  borderRadius: 18,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  backgroundColor: '#151515',
  borderWidth: 1,
  borderColor: 'rgba(255,255,255,0.06)',
};

/**
 * CORE-1872 (QA 29.10) — запись комнаты — HLS (`.m3u8` + TS). Браузер со своим HLS
 * играет её из голого `src` без CORS; браузер без него отвечает `MEDIA_ERR_SRC_NOT_SUPPORTED`
 * (код 4) — подписчик видел «Скрыть запись» и пустой плеер. Тогда, и только для HLS,
 * переходим на hls.js. Сперва — родной путь: hls.js качает сегменты через `fetch` и упирается
 * в CORS бакета, которого у части адресов фронта нет.
 */
export function shouldFallBackToHlsJs(url: string | null | undefined, mediaErrorCode: number | null | undefined): boolean {
  return isHlsUrl(url) && mediaErrorCode === 4;
}

/**
 * Как `<video>` открывает HLS-запись: `native` — голый `src` без CORS, `cors` — тот же
 * `src` с `crossOrigin="anonymous"`, `hlsjs` — через hls.js.
 */
export type HlsLoadMode = 'native' | 'cors' | 'hlsjs';

/**
 * CORE-1909 (возврат QA 30.10) — Chrome со своим HLS отклонял запись без CORS
 * («insecure media request»), а с `crossorigin="anonymous"` все три фрагмента играли.
 * Сразу ставить CORS нельзя: у части адресов фронта (ru.agapella.io, dev.agdev) в CORS
 * бакета их нет, и там играет только голый `src`. Поэтому лесенка по ошибке:
 * без CORS → с CORS → hls.js. Код 4 (своего HLS нет) — сразу в hls.js, как в CORE-1872.
 * `null` — пробовать больше нечего.
 */
export function nextHlsLoadMode(
  url: string | null | undefined,
  mode: HlsLoadMode,
  mediaErrorCode: number | null | undefined,
): HlsLoadMode | null {
  if (!isHlsUrl(url)) return null;
  if (mode === 'native') return shouldFallBackToHlsJs(url, mediaErrorCode) ? 'hlsjs' : 'cors';
  if (mode === 'cors') return 'hlsjs';
  return null;
}

/**
 * CORE-1909 (возврат QA 30.10, Safari) — «Только звук»: флаг бэкенда решает, если он
 * есть, в обе стороны; замер кадра — только когда бэкенд не знает. Safari на
 * `loadedmetadata` у HLS отдаёт кадр 0 × 0 и размер сообщает позже (`resize`), так
 * что замер не должен перебивать `hasVideo: true`.
 */
export function isRecordingAudioOnly(hasVideo: boolean | null | undefined, probedAudioOnly: boolean): boolean {
  if (hasVideo === true) return false;
  if (hasVideo === false) return true;
  return probedAudioOnly;
}

export function RoomRecordingPlayer({ playbackUrl: rawPlaybackUrl, state = 'None', coverImageUrl, hasVideo, endedAtUtc, telemetry, locked, onOpenPaywall }: Props) {
  // CORE-1919 — тот же файл записи с новым сроком не перезапускает плеер.
  const playbackUrl = useStableMediaUrl(rawPlaybackUrl);
  // CORE-1872 / CORE-1909 — ступень загрузки HLS для текущего адреса (см. `nextHlsLoadMode`).
  // Привязана к адресу: другой фрагмент снова начинает с родного пути без CORS.
  const [hlsLoad, setHlsLoad] = useState<{ url: string; mode: HlsLoadMode } | null>(null);
  const loadMode: HlsLoadMode = playbackUrl && hlsLoad?.url === playbackUrl ? hlsLoad.mode : 'native';
  const useHlsJs = Platform.OS === 'web' && !!playbackUrl && loadMode === 'hlsjs';
  // CORE-859 — arm a one-shot timeout so a stuck Recording/Processing state
  // eventually reads as terminal. Seeded synchronously (handles a screen opened
  // long after the deadline already passed) and re-armed whenever the inputs
  // change. Only relevant while the state is non-terminal.
  const [stalled, setStalled] = useState(() =>
    isRecordingProcessingStalled(state, endedAtUtc, Date.now()),
  );
  const isNonTerminal = state === 'Recording' || state === 'Processing';
  useEffect(() => {
    if (!isNonTerminal || !endedAtUtc) {
      setStalled(false);
      return;
    }
    const ended = Date.parse(endedAtUtc);
    if (Number.isNaN(ended)) {
      setStalled(false);
      return;
    }
    const remaining = ended + RECORDING_PROCESSING_TIMEOUT_MS - Date.now();
    if (remaining <= 0) {
      setStalled(true);
      return;
    }
    setStalled(false);
    const id = setTimeout(() => setStalled(true), remaining);
    return () => clearTimeout(id);
  }, [state, endedAtUtc, isNonTerminal]);

  const kind = getRecordingDisplayKind(state, playbackUrl, { processingStalled: stalled, locked });
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // CORE-1573 — адрес, у которого на `loadedmetadata` кадр оказался 0 × 0.
  // Привязан к адресу: новая запись начинает с «изображение есть».
  const [audioOnlyUrl, setAudioOnlyUrl] = useState<string | null>(null);
  // No-op on non-web / kind !== 'video' since the <video> ref stays null.
  useRecordingPlaybackTelemetry(videoRef, telemetry ?? null);

  // CORE-1909 — смена `crossOrigin` действует только на новую загрузку.
  useEffect(() => {
    if (Platform.OS === 'web' && loadMode === 'cors') videoRef.current?.load();
  }, [loadMode, playbackUrl]);

  useEffect(() => {
    const video = videoRef.current;
    if (!useHlsJs || !video || !playbackUrl) return;
    let cancelled = false;
    let hls: { destroy: () => void } | null = null;
    import('hls.js').then(({ default: Hls }) => {
      if (cancelled || !Hls.isSupported()) return;
      const instance = new Hls();
      hls = instance;
      instance.loadSource(playbackUrl);
      instance.attachMedia(video);
    }).catch(() => { /* модуль не загрузился — остаются штатные контролы */ });
    return () => {
      cancelled = true;
      hls?.destroy();
    };
  }, [useHlsJs, playbackUrl]);

  if (kind === 'video') {
    if (Platform.OS === 'web') {
      // CORE-1573 — запись без изображения (аудиомост пишет только звук) не
      // рисуется чёрным кадром: тот же элемент остаётся полосой управления
      // поверх обложки, а экран прямо говорит, что в записи только звук.
      // CORE-1383 — бэкенд знает про аудиозапись заранее; замер кадра остаётся
      // страховкой для записей, у которых флаг ещё не проставлен.
      const audioOnly = isRecordingAudioOnly(hasVideo, audioOnlyUrl === playbackUrl);
      // CORE-1909 (Safari) — кадр появился позже `loadedmetadata`: снять «Только звук».
      const recheckFrame = (e: any) => {
        const el = e.currentTarget as HTMLVideoElement;
        if (audioOnlyUrl === playbackUrl && hasVideoFrame(el)) {
          setAudioOnlyUrl(null);
          recordAudioOnlyProbe(playbackUrl, false);
        }
      };
      return (
        <View testID={audioOnly ? 'room-recording-audio-only' : undefined} style={{ width: '100%', aspectRatio: 16 / 9, borderRadius: 18, overflow: 'hidden', backgroundColor: '#151515' }}>
          {audioOnly && (
            <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } as any}>
              {coverImageUrl ? (
                <ImageBackground source={{ uri: coverImageUrl }} style={{ flex: 1 } as any} resizeMode="cover">
                  <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.55)' } as any} />
                </ImageBackground>
              ) : null}
              <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: AUDIO_ONLY_CONTROLS_HEIGHT, alignItems: 'center', justifyContent: 'center', gap: 8, paddingHorizontal: 24 } as any}>
                <View style={{
                  flexDirection: 'row', alignItems: 'center', gap: 8,
                  paddingHorizontal: 16, paddingVertical: 9, borderRadius: 999,
                  backgroundColor: 'rgba(0,0,0,0.55)',
                  borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)',
                } as any}>
                  <Ionicons name="musical-notes" size={16} color="#E5B567" />
                  <Text style={{ color: '#FFFFFF', fontSize: 14, fontWeight: '700' }}>Только звук</Text>
                </View>
                <Text style={{ color: '#D4D4D8', fontSize: 12.5, textAlign: 'center' }}>
                  Изображение в эту запись не попало
                </Text>
              </View>
            </View>
          )}
          <video
            ref={videoRef as any}
            src={useHlsJs ? undefined : (playbackUrl ?? undefined)}
            crossOrigin={loadMode === 'cors' ? 'anonymous' : undefined}
            controls
            onError={(e: any) => {
              if (useHlsJs || !playbackUrl) return;
              const code = (e.currentTarget as HTMLVideoElement).error?.code;
              const next = nextHlsLoadMode(playbackUrl, loadMode, code);
              if (next) setHlsLoad({ url: playbackUrl, mode: next });
            }}
            onResize={recheckFrame}
            onLoadedData={recheckFrame}
            onLoadedMetadata={(e: any) => {
              const el = e.currentTarget as HTMLVideoElement;
              const frame = hasVideoFrame(el);
              setAudioOnlyUrl(frame ? null : (playbackUrl ?? null));
              // CORE-1383 (возврат QA 06.10) — карточка архива узнаёт то же,
              // что увидел плеер: при `hasVideo: null` от бэкенда у неё
              // другого источника нет.
              recordAudioOnlyProbe(playbackUrl, !frame);
            }}
            style={(audioOnly
              ? { position: 'absolute', left: 0, right: 0, bottom: 0, width: '100%', height: AUDIO_ONLY_CONTROLS_HEIGHT, background: 'transparent' }
              : { width: '100%', height: '100%' }) as any}
          />
        </View>
      );
    }
    // Нативная запись — `expo-video` через общий проигрыватель практик. Раньше
    // здесь стояла заглушка «доступна по ссылке»: посмотреть свою же практику в
    // приложении было нельзя. Реализация живёт в `.native`-близнеце, чтобы
    // `expo-video` не попадал в веб-бандл, где ветка выше уже отработала.
    return <RoomRecordingVideo playbackUrl={playbackUrl ?? null} posterUri={coverImageUrl} audioOnly={hasVideo === false} />;
  }

  // CORE-1780 — тот же вид, что у закрытого эфира (`AgapellaLockedPlayer`), но
  // на голых примитивах: помощники этого файла грузят тесты без UI-кита.
  if (kind === 'locked') {
    const body = (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12, paddingHorizontal: 24, backgroundColor: 'rgba(0,0,0,0.55)' } as any}>
        <View style={{
          flexDirection: 'row', alignItems: 'center', gap: 6,
          paddingHorizontal: 12, paddingVertical: 6, borderRadius: 999,
          backgroundColor: 'rgba(0,0,0,0.55)',
          borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)',
        } as any}>
          <Ionicons name="sparkles-outline" size={13} color="#E5B567" />
          <Text style={{ color: '#FFFFFF', fontSize: 12.5, fontWeight: '700' }}>{LOCKED_RECORDING_BADGE}</Text>
        </View>
        <Ionicons name="lock-closed-outline" size={30} color="rgba(255,255,255,0.85)" />
        <Text style={{ color: '#FFFFFF', fontSize: 16, fontWeight: '600', textAlign: 'center' }}>{LOCKED_RECORDING_TITLE}</Text>
        <TouchableOpacity
          testID="room-recording-agapella-open"
          accessibilityRole="button"
          activeOpacity={0.85}
          onPress={onOpenPaywall}
          style={{ marginTop: 4, paddingHorizontal: 22, paddingVertical: 11, borderRadius: 999, backgroundColor: '#E98245' }}
        >
          <Text style={{ color: '#FFFFFF', fontSize: 14, fontWeight: '600' }}>{LOCKED_RECORDING_ACTION}</Text>
        </TouchableOpacity>
      </View>
    );
    return (
      <View testID="room-recording-agapella-locked" style={{ width: '100%', aspectRatio: 16 / 9, borderRadius: 18, overflow: 'hidden', backgroundColor: '#151515' }}>
        {coverImageUrl ? (
          <ImageBackground source={{ uri: coverImageUrl }} style={{ flex: 1 } as any} resizeMode="cover">{body}</ImageBackground>
        ) : body}
      </View>
    );
  }

  if (kind === 'processing') {
    return (
      <View style={{ ...FRAME_STYLE, gap: 10, paddingHorizontal: 24 }}>
        <ActivityIndicator size="small" color="#F4F4F5" />
        <Text style={{ color: '#F4F4F5', fontSize: 15, fontWeight: '600', textAlign: 'center' }}>
          Запись готовится
        </Text>
        <Text style={{ color: '#A1A1AA', fontSize: 13, textAlign: 'center' }}>
          Обычно 3–5 минут после окончания Live-сессии.
        </Text>
      </View>
    );
  }

  // 'stalled' — CORE-859. The recording sat in Recording/Processing past the
  // deadline and the backend never sent a terminal event. Stop promising
  // «готовится» (which hung forever) and show an honest terminal fallback with
  // a next step, so the user isn't left guessing whether the record is lost.
  if (kind === 'stalled') {
    return (
      <View style={{ ...FRAME_STYLE, gap: 10, paddingHorizontal: 24 }}>
        <Ionicons name="time-outline" size={22} color="#E5B567" />
        <Text style={{ color: '#F4F4F5', fontSize: 15, fontWeight: '700', textAlign: 'center' }}>
          Запись не готова
        </Text>
        <Text style={{ color: '#A1A1AA', fontSize: 13, textAlign: 'center', lineHeight: 18 }}>
          Обработка заняла больше обычного. Обновите страницу чуть позже — если записи всё
          ещё нет, напишите нам, поможем разобраться.
        </Text>
      </View>
    );
  }

  // 'failed' — CORE-229 / CORE-1327. Записи нет. Без зелёной галки и без
  // кнопки воспроизведения: экран говорит, что сессия не сохранилась.
  //
  // CORE-1327 — но НЕ говорит почему. Прежний текст утверждал «никто не
  // включал камеру и микрофон», потому что вердикт `Failed` ставится по
  // пустому префиксу в S3, а пустой префикс читался как молчащий канал.
  // На прод-комнате 212 это оказалось ложью: камера публиковала (uid
  // 124832979, 17:20:02–17:23:08), а префикс пуст потому, что запись прожила
  // 346 мс. Причин у пустого префикса несколько, экран их не различает —
  // значит и называть одну из них не должен.
  //
  // Утверждать причину дороже, чем промолчать о ней: человек, которому
  // сказали «вы не включали камеру», не пойдёт разбираться дальше.
  if (kind === 'failed') {
    const badge = (
      <View style={{
        gap: 6, alignItems: 'center',
        paddingHorizontal: 18, paddingVertical: 12, borderRadius: 16,
        backgroundColor: 'rgba(0,0,0,0.55)',
        borderWidth: 1, borderColor: 'rgba(255,255,255,0.14)',
        maxWidth: 320,
      } as any}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 } as any}>
          <Ionicons name="videocam-off-outline" size={18} color="#E5B567" />
          <Text style={{ color: '#F4F4F5', fontSize: 14, fontWeight: '700' }}>
            Запись не сохранилась
          </Text>
        </View>
        <Text style={{ color: '#D4D4D8', fontSize: 12.5, textAlign: 'center', lineHeight: 17 }}>
          Файла этой Live-сессии не осталось. Если запись была важна, напишите нам — посмотрим, что случилось.
        </Text>
      </View>
    );
    if (coverImageUrl) {
      return (
        <View style={{ width: '100%', aspectRatio: 16 / 9, borderRadius: 18, overflow: 'hidden' }}>
          <ImageBackground
            source={{ uri: coverImageUrl }}
            style={{ flex: 1 } as any}
            resizeMode="cover"
          >
            <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.5)' } as any}>
              {badge}
            </View>
          </ImageBackground>
        </View>
      );
    }
    return (
      <View style={{ ...FRAME_STYLE, paddingHorizontal: 20 }}>
        {badge}
      </View>
    );
  }

  // 'unavailable' — None. The room simply ended without a recording having
  // been made. Show the practice cover with a soft «ЭФИР ЗАВЕРШЁН» badge so the
  // surface reads as the practice the user attended, not «missing content».
  // (Owner directive 2026-05-08.)
  if (coverImageUrl) {
    return (
      <View style={{ width: '100%', aspectRatio: 16 / 9, borderRadius: 18, overflow: 'hidden' }}>
        <ImageBackground
          source={{ uri: coverImageUrl }}
          style={{ flex: 1, justifyContent: 'flex-end' } as any}
          resizeMode="cover"
        >
          <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: 'rgba(0,0,0,0.45)' } as any}>
            <View style={{
              flexDirection: 'row', alignItems: 'center', gap: 8,
              paddingHorizontal: 16, paddingVertical: 9, borderRadius: 999,
              backgroundColor: 'rgba(0,0,0,0.55)',
              borderWidth: 1, borderColor: 'rgba(255,255,255,0.18)',
            } as any}>
              <Ionicons name="checkmark-circle" size={16} color="#16A34A" />
              <Text style={{ color: '#FFFFFF', fontSize: 14, fontWeight: '700', letterSpacing: 0.5 }}>
                LIVE-СЕССИЯ ЗАВЕРШЕНА
              </Text>
            </View>
          </View>
        </ImageBackground>
      </View>
    );
  }
  // No cover available — keep a minimal dark frame with the same badge
  // (no «recording unavailable» copy). Avoids a blank black box.
  return (
    <View style={{ ...FRAME_STYLE }}>
      <View style={{
        flexDirection: 'row', alignItems: 'center', gap: 8,
        paddingHorizontal: 16, paddingVertical: 9, borderRadius: 999,
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderWidth: 1, borderColor: 'rgba(255,255,255,0.10)',
      } as any}>
        <Ionicons name="checkmark-circle" size={16} color="#16A34A" />
        <Text style={{ color: '#F4F4F5', fontSize: 14, fontWeight: '700', letterSpacing: 0.5 }}>
          LIVE-СЕССИЯ ЗАВЕРШЕНА
        </Text>
      </View>
    </View>
  );
}
